/**
 * @module experience/byod/compare-devices
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  HARDGOODS_INFINITE_SCROLL_CONSTANTS,
  HARDGOODS_CONSTANTS,
  DIGITALDATA_CONSTANT,
} from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { HardgoodsDeviceDetails } from '@experience/shop/hardgoods/interfaces/browse-hardgood.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { Subject } from 'rxjs';
import { BrowseDeviceModel } from '@experience/shop/hardgoods/models/browse-device.model';
import { FilterCriteria } from '@core/aem/interfaces/shop/browse-hardgoods.interface';
import { BrowseHardgoodsService } from '@experience/shop/hardgoods/services/browse-hardgoods.service';
import { APIError } from '@support/api/common/error.interface';
import { Filter, SortOptions } from '@support/api/common/product.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { CartSupportService } from '@support/cart-support/services/cart-support.service';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { TmoButton } from '@support/shared/widgets/interfaces/button.interface';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'tmo-accessory-category-details',
  templateUrl: './accessory-category-details.component.html',
  styleUrls: [
    '../../../../../../style-sheets/components/page-titles.component.scss',
    '../../../../../../style-sheets/components/input-checkbox.component.scss',
  ],
})
export class AccessoryCategoryDetailsComponent
  extends PDLBaseComponent
  implements OnInit, OnDestroy
{
  /**
   *  Boolean flag to check if hardgoods have been loaded
   */
  public isLoading: boolean;

  /**
   * Contain page number to send in request payload while making 2100 api call
   */
  public pageNumber: number;

  /**
   * Holds to show component level spinner while making 2100 from second Time.
   */
  public showComponentSpinner: boolean;

  /**
   * Checks if the entry config data has been initialized
   */
  public configDataLoaded: boolean;

  /**
   * Checks if the entry config data has been initialized
   */
  public filters: Filter[];

  /**
   * Stores the sort options
   */
  public sorts: SortOptions[];

  /**
   * Stores the sort options
   */
  public category: string;

  /**
   * Next button to be displayed in Html
   */
  public seeMore: TmoButton;
  /**
   * Stores the hardGoods data
   */
  public hardGoods: BrowseDeviceModel[];

  /**
   * Stores AEM data
   */
  public aemConfigurableData: ConfigData;
  /**
   * Stores product availabe
   */
  public isProductAvailable: boolean;

  /**
   * Flag to show or hide the error message
   */
  public showErrorMessage: boolean;

  /**
   * filter criteria array to store previous selected values
   */
  public previousSelectedValues: FilterCriteria[];
  /**
   * Variable to store total count for accessories
   */
  public deviceCount: number;
  /**
   * Un-subscriber for all subscriptions
   */
  private ngUnsubscribe: Subject<void>;

  constructor(
    private browseHardgoodsService: BrowseHardgoodsService,
    private cartSupportService: CartSupportService,
    private objectService: ObjectService,
    private route: ActivatedRoute,
    public routeSnapshot: ActivatedRoute,
    public pdlService: PdlService
  ) {
    super(pdlService, routeSnapshot);
    this.ngUnsubscribe = new Subject<void>();
    this.pageNumber = HARDGOODS_INFINITE_SCROLL_CONSTANTS.PAGENUMBER;
    this.showErrorMessage = false;
    this.seeMore = {
      id: 'seeMore',
      name: 'seeMore',
      class: 'btn btn-primary',
      type: 'Primary',
    };
  }

  /**
   * ngOnInit to initialize the component data
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.loadAemContent();
    this.category = this.route.snapshot.queryParams.category;
    this.browseHardgoodsService.hardGoodDataObservable$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((hardgoodData: HardgoodsDeviceDetails) => {
        this.hardGoods = this.objectService.get(hardgoodData, 'productModels');
        const deviceCount = this.objectService.get(hardgoodData, 'metadata.totalRecordCount');
        this.deviceCount = deviceCount ? deviceCount : 0;
        this.isProductAvailable = this.objectService.get(hardgoodData, 'isProductAvailable');
      });
  }

  /**
   * ngOnDestroy to clear the variables when component scope destroys
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Makes API call after reaching to bottem of page to get next 24 values
   */
  public onClickSeeAll(): void {
    this.pageNumber++;
    this.getAccessoryData();
  }

  /**
   * Method used to get list of hardgoods from API and store in class variable
   * @returns void
   */
  public getAccessoryData(): void {
    if (this.pageNumber !== 1) {
      this.showComponentSpinner = true;
    } else {
      this.hardGoods = [];
    }
    this.browseHardgoodsService
      .loadHardgoodsData(
        HARDGOODS_CONSTANTS.BROWSE_ACCESSORIES,
        this.pageNumber,
        false,
        this.category
      )
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe(
        (hardgoods: HardgoodsDeviceDetails) => {
          if (this.objectService.get(hardgoods, 'productModels').length > 0) {
            this.showErrorMessage = false;
            this.configDataLoaded = true;
            this.filters = this.objectService.get(hardgoods, 'filter');
            this.sorts = this.objectService.get(hardgoods, 'sort');
            this.isProductAvailable = this.objectService.get(hardgoods, 'isProductAvailable');
            this.hardGoods = this.hardGoods.concat(
              this.objectService.get(hardgoods, 'productModels')
            );
            this.showComponentSpinner = false;
          }
        },
        (error: APIError) => {
          this.showComponentSpinner = false;
          this.showErrorMessage = true;
        }
      );
  }

  /**
   * Load AEM content for ACDP
   * @returns void
   */
  private loadAemContent(): void {
    this.cartSupportService
      .getAEMContent(HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_ROUTE)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((aemData: ConfigData) => {
        if (aemData) {
          this.aemConfigurableData = this.objectService.get(aemData, 'configurableData');
          // window[DIGITALDATA_CONSTANT.DIGITALDATA][DIGITALDATA_CONSTANT.PAGE][DIGITALDATA_CONSTANT.PAGEINFO][DIGITALDATA_CONSTANT.SUBCATEGORY] = `Accessories : ${this.route.snapshot.queryParams.category}`;
          window[DIGITALDATA_CONSTANT.DIGITALDATA][DIGITALDATA_CONSTANT.PAGE][
            DIGITALDATA_CONSTANT.PAGEINFO
          ][DIGITALDATA_CONSTANT.PAGENAME] =
            `Accessories : ${this.route.snapshot.queryParams.category}`;
          this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
        }
      });
  }
}
